/**
 * Referral Code Management Utility
 * 
 * Handles capturing, storing, and retrieving referral codes from URL parameters.
 * Referral codes are persisted in localStorage to survive navigation across the app.
 */

const STORAGE_KEY = 'futurasmile_referral_code';
const EXPIRY_DAYS = 7;

interface StoredReferralCode {
    code: string;
    timestamp: number;
}

/**
 * Captures referral code from URL query parameter (?ref=CODE)
 * Stores it in localStorage with timestamp for expiry tracking
 * 
 * Call this on app initialization or route changes to automatically
 * capture referral codes from any page.
 */
export const captureReferralCode = (): void => {
    try {
        // Parse current URL for ref parameter
        const urlParams = new URLSearchParams(window.location.search);
        const refCode = urlParams.get('ref');

        if (refCode) {
            // Store with timestamp
            const data: StoredReferralCode = {
                code: refCode.trim(),
                timestamp: Date.now()
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
            console.log('[Referral] Captured referral code:', refCode);
        }
    } catch (error) {
        console.error('[Referral] Failed to capture referral code:', error);
    }
};

/**
 * Retrieves the stored referral code if it exists and hasn't expired
 * 
 * @returns The referral code string, or null if none exists or expired
 */
export const getReferralCode = (): string | null => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return null;

        const data: StoredReferralCode = JSON.parse(stored);
        const now = Date.now();
        const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

        // Check if expired
        if (now - data.timestamp > expiryMs) {
            console.log('[Referral] Code expired, clearing');
            clearReferralCode();
            return null;
        }

        return data.code;
    } catch (error) {
        console.error('[Referral] Failed to retrieve referral code:', error);
        return null;
    }
};

/**
 * Clears the stored referral code
 * Should be called after successful registration to prevent reuse
 */
export const clearReferralCode = (): void => {
    try {
        localStorage.removeItem(STORAGE_KEY);
        console.log('[Referral] Cleared referral code');
    } catch (error) {
        console.error('[Referral] Failed to clear referral code:', error);
    }
};

/**
 * Checks if a referral code is currently stored
 * 
 * @returns true if a valid referral code exists
 */
export const hasReferralCode = (): boolean => {
    return getReferralCode() !== null;
};
